import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import {
  deleteFeatureFlag,
  getPlatformSettingsAdmin,
  updateFeatureFlag,
  updateMaintenanceMode,
} from "@/lib/platform-settings.functions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/admin/settings")({
  component: AdminSettingsPage,
});

function AdminSettingsPage() {
  const fetchSettings = useServerFn(getPlatformSettingsAdmin);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-platform-settings"],
    queryFn: () => fetchSettings(),
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <MaintenanceCard
        initial={data.maintenance.value}
        updatedAt={data.maintenance.updated_at}
      />
      <FeatureFlagsCard flags={data.flags.value} updatedAt={data.flags.updated_at} />
    </div>
  );
}

function MaintenanceCard({
  initial,
  updatedAt,
}: {
  initial: { enabled: boolean; message: string };
  updatedAt: string | null;
}) {
  const qc = useQueryClient();
  const update = useServerFn(updateMaintenanceMode);
  const [enabled, setEnabled] = useState(initial.enabled);
  const [message, setMessage] = useState(initial.message);

  const mutation = useMutation({
    mutationFn: (vars: { enabled: boolean; message: string }) => update({ data: vars }),
    onSuccess: () => {
      toast.success("Maintenance settings saved");
      qc.invalidateQueries({ queryKey: ["admin-platform-settings"] });
      qc.invalidateQueries({ queryKey: ["platform-status"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dirty = enabled !== initial.enabled || message !== initial.message;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Read-only maintenance mode</CardTitle>
        <CardDescription>
          When enabled, non-admin users see a banner and all write operations (AI calls, imports,
          mutations using the read-only guard) are blocked. Platform admins bypass.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div>
            <Label htmlFor="mm-enabled" className="text-sm font-medium">
              Enable maintenance mode
            </Label>
            <p className="text-xs text-muted-foreground">
              {enabled ? "Platform is read-only for non-admins" : "Platform is fully operational"}
            </p>
          </div>
          <Switch id="mm-enabled" checked={enabled} onCheckedChange={setEnabled} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="mm-message">Banner message</Label>
          <Textarea
            id="mm-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="We're performing maintenance…"
          />
          <p className="text-xs text-muted-foreground">
            Shown to every user when maintenance is enabled. {message.length}/500
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            {updatedAt ? `Last updated ${new Date(updatedAt).toLocaleString()}` : "Never updated"}
          </p>
          <Button
            disabled={!dirty || mutation.isPending}
            onClick={() => mutation.mutate({ enabled, message })}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function FeatureFlagsCard({
  flags,
  updatedAt,
}: {
  flags: Record<string, boolean>;
  updatedAt: string | null;
}) {
  const qc = useQueryClient();
  const update = useServerFn(updateFeatureFlag);
  const remove = useServerFn(deleteFeatureFlag);
  const [newFlag, setNewFlag] = useState("");

  const toggle = useMutation({
    mutationFn: (vars: { flag: string; enabled: boolean }) => update({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-platform-settings"] });
      qc.invalidateQueries({ queryKey: ["platform-status"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (flag: string) => remove({ data: { flag } }),
    onSuccess: () => {
      toast.success("Flag removed");
      qc.invalidateQueries({ queryKey: ["admin-platform-settings"] });
      qc.invalidateQueries({ queryKey: ["platform-status"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const entries = Object.entries(flags).sort(([a], [b]) => a.localeCompare(b));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Feature flags</CardTitle>
        <CardDescription>
          Global on/off switches. Use in code via{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">flags[&apos;your-flag&apos;]</code>{" "}
          from <code className="rounded bg-muted px-1 py-0.5 text-xs">getPlatformStatus</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1 space-y-2">
            <Label htmlFor="new-flag">Add new flag</Label>
            <Input
              id="new-flag"
              value={newFlag}
              onChange={(e) => setNewFlag(e.target.value.toLowerCase())}
              placeholder="my-new-feature"
              pattern="[a-z0-9_-]+"
            />
          </div>
          <Button
            onClick={() => {
              if (!newFlag) return;
              toggle.mutate(
                { flag: newFlag, enabled: false },
                { onSuccess: () => setNewFlag("") },
              );
            }}
            disabled={!newFlag || toggle.isPending}
          >
            <Plus className="mr-2 h-4 w-4" /> Add
          </Button>
        </div>

        <div className="divide-y divide-border rounded-lg border border-border">
          {entries.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">
              No feature flags defined yet.
            </p>
          ) : (
            entries.map(([flag, enabled]) => (
              <div key={flag} className="flex items-center justify-between gap-3 p-3">
                <div>
                  <code className="text-sm font-medium">{flag}</code>
                  <p className="text-xs text-muted-foreground">
                    {enabled ? "Enabled globally" : "Disabled"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={enabled}
                    onCheckedChange={(v) => toggle.mutate({ flag, enabled: v })}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => del.mutate(flag)}
                    disabled={del.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {updatedAt && (
          <p className="text-xs text-muted-foreground">
            Last updated {new Date(updatedAt).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
